/**
 * ExistingPatientBooking — Dashboard de reagendamiento para pacientes existentes.
 * Estructura: bienvenida → próximas sesiones → agendar nueva hora → historial.
 * Sin alert() ni confirm() — feedback mediante modal inline y toast.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBooking } from './BookingContext';
import { BookingServiceFixed } from '../../services/booking-fixed.service';
import { RescheduleService } from '../../services/reschedule.service';
import { EmailService } from '../../services/email.service';
import { TransbankService } from '../../services/transbank.service';
import { saveNombrePareja, PAREJA_NUDGE_LOCALSTORAGE_KEY } from '../../services/roadmap.service';
import { useTerapeutas } from '../../hooks/useTerapeutas';
import { CLINIC_OFFICE_ADDRESS } from '../../data/clinicPaymentInfo';
import { getSessionPrice } from '../../lib/utils/pricing';
import PaymentMethodSelector from './PaymentMethodSelector';
import CalendarSelectorImproved from './steps/CalendarSelectorImproved';
import './ExistingPatientBooking.css';

// ─── Tipos locales ────────────────────────────────────────────────────────────

interface Consultation {
  id: string;
  fecha_hora: string;
  duracion: number;
  tipo_consulta: string;
  modalidad: string;
  estado: string;
  valor_consulta?: number;
  descuento?: number;
  valor_final?: number;
  terapeuta_id?: string;
}

interface ConfirmModal {
  type: 'confirm' | 'cancel' | 'reschedule';
  consultation: Consultation;
}

interface Toast {
  id: number;
  message: string;
  variant: 'success' | 'error';
}

interface ExistingPatientBookingProps {
  onComplete: (bookingId?: string, meetLink?: string) => void;
  onBack: () => void;
  /** FUNC-125: avisa al wizard que se pasó el gate de contacto (habilita el mapa de la ruta). */
  onContactConfirmed?: () => void;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const THERAPY_TYPES = [
  { id: 'pareja', name: 'Terapia de Parejas', icon: 'fas fa-heart', color: '#C05E00' },
  { id: 'individual', name: 'Terapia Individual', icon: 'fas fa-user', color: '#1A6FBF' },
] as const;

type TherapyTypeId = typeof THERAPY_TYPES[number]['id'];

const MODALITY_OPTIONS = [
  { id: 'presencial' as const, name: 'Presencial', icon: 'fas fa-building', description: 'En nuestras oficinas' },
  { id: 'online' as const, name: 'Online', icon: 'fas fa-video', description: 'Videollamada' },
  // Solo se muestra cuando selectedTherapyType === 'pareja' (ver filter en el render).
  { id: 'domicilio' as const, name: 'Domicilio', icon: 'fas fa-home', description: 'El terapeuta viaja a tu hogar' },
];

type DurationMinutes = 45 | 50 | 90;

// Solo duraciones: el precio sale de getSessionPrice (lib/utils/pricing.ts), que
// depende de modalidad y método de pago. Familia deshabilitada (FUNC-131).
const DURATIONS_BY_TYPE: Record<TherapyTypeId, { minutes: DurationMinutes; name: string }[]> = {
  pareja: [
    { minutes: 50, name: '50 minutos' },
    { minutes: 90, name: '90 minutos' },
  ],
  individual: [
    { minutes: 45, name: '45 minutos' },
    { minutes: 90, name: '90 minutos' },
  ],
};

const LOCATIONS = [
  { id: 'santiago-centro', name: 'Santiago Centro', address: CLINIC_OFFICE_ADDRESS },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

let toastCounter = 0;

function formatDate(iso: string): string {
  // Date-only strings (YYYY-MM-DD) are treated as UTC midnight by the Date constructor,
  // which shifts the date back one day in UTC- timezones (Chile is UTC-3/UTC-4).
  // Appending T12:00:00 forces local noon interpretation and avoids the off-by-one.
  const normalized = iso.includes('T') ? iso : `${iso}T12:00:00`;
  return new Date(normalized).toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function therapyColor(tipo: string): string {
  const map: Record<string, string> = {
    individual: '#1A6FBF',
    pareja: '#C05E00',
    familia: '#057A55',
    sexologia: '#6B21A8',
  };
  return map[tipo?.toLowerCase()] ?? 'var(--primary)';
}

// Feature domicilio: helpers compartidos para no repetir el ternario binario
// presencial/online en cada lugar que muestra la modalidad (esta vista tiene
// varios: síntesis, tarjetas de próximas sesiones, historial, resumen).
function modalityLabel(modalidad: string): string {
  if (modalidad === 'presencial') return 'Presencial';
  if (modalidad === 'domicilio') return 'Domicilio';
  return 'Online';
}

function modalityIcon(modalidad: string): string {
  if (modalidad === 'presencial') return 'fa-building';
  if (modalidad === 'domicilio') return 'fa-home';
  return 'fa-video';
}

// ─── Componente principal ─────────────────────────────────────────────────────

const ExistingPatientBooking: React.FC<ExistingPatientBookingProps> = ({
  onComplete,
  onBack,
  onContactConfirmed,
}) => {
  const { state, dispatch, setService, setTherapist, clearBooking, setMetodoPagoDeclarado, setParejaNudge } = useBooking();
  const { terapeutasExtended, loading: terapeutasLoading } = useTerapeutas();

  // Selección de nueva cita
  const [selectedTherapyType, setSelectedTherapyType] = useState<TherapyTypeId>('pareja');
  const [selectedModality, setSelectedModality] = useState<'presencial' | 'online' | 'domicilio'>('presencial');
  const [selectedDuration, setSelectedDuration] = useState<DurationMinutes>(50);
  const [selectedLocation, setSelectedLocation] = useState<string>('santiago-centro');
  const [showNewBookingPanel, setShowNewBookingPanel] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showSynthesis, setShowSynthesis] = useState(false);
  const [isBooking, setIsBooking] = useState(false);

  // Consultas existentes
  const [existingConsultations, setExistingConsultations] = useState<Consultation[]>([]);
  const [pastConsultations, setPastConsultations] = useState<Consultation[]>([]);
  const [loadingConsultations, setLoadingConsultations] = useState(true);
  const [historialExpanded, setHistorialExpanded] = useState(false);

  // Reagendamiento
  const [showRescheduleCalendar, setShowRescheduleCalendar] = useState(false);
  const [consultationToReschedule, setConsultationToReschedule] = useState<Consultation | null>(null);
  const [isRescheduling, setIsRescheduling] = useState(false);

  // Modal de confirmación inline
  const [confirmModal, setConfirmModal] = useState<ConfirmModal | null>(null);
  const [actionInProgress, setActionInProgress] = useState<Record<string, boolean>>({});

  // Toast
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Roadmap Feature 1 — nudge de nombre_pareja (docs/plan-ruta-terapeutica-pareja.md §6.3).
  // `state.parejaNudge` lo calcula BookingWizardContent (única llamada a la RPC, compartida
  // con Konex). El dismiss ("ahora no") es solo de esta sesión — no persiste, así que la
  // tarjeta vuelve a aparecer en la próxima visita, tal como pide el plan.
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [nudgeInput, setNudgeInput] = useState('');
  const [savingNudge, setSavingNudge] = useState(false);
  const showParejaNudgeCard = state.parejaNudge === true && !nudgeDismissed;

  // Ancla del autoscroll al abrir "Agendar nueva hora".
  const newBookingSectionRef = useRef<HTMLElement | null>(null);

  const patient = state.patientVerification?.existingPatient;
  const preferredTherapist = terapeutasExtended.find(t => t.id === patient?.therapistId);

  // ── Contacto del paciente (SEC-057 → FUNC-126) ───────────────────────────
  // search_patient_by_rut no devuelve email/telefono (SEC-057) y el paciente
  // entra SOLO con su RUT — no se le pide contacto. Los correos al paciente
  // mandan consultaId + RUT y send-email resuelve destinatario y contenido
  // desde la ficha con service_role: el contacto nunca llega al navegador.
  // (El gate de contacto anterior no verificaba lo tipeado — no aportaba
  // seguridad, solo fricción.) Sin email en ficha, la cita se crea igual y
  // solo se omite el correo.
  useEffect(() => {
    if (patient?.id) onContactConfirmed?.();
    // onContactConfirmed es un callback inline del wizard — no re-disparar por identidad.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  const patientEmailTarget = (consultaId: string) => ({
    consultaId,
    patientRut: patient?.rut ?? '',
  });

  // Doble rAF: si el scroll smooth corre en el mismo frame que una animación de
  // Framer Motion (whileTap del botón), el browser lo descarta sin moverse un
  // píxel. Mismo patrón que ServiceTherapistSelector.tsx.
  const scrollIntoViewSoon = (
    ref: React.RefObject<HTMLElement | null>,
    block: ScrollLogicalPosition,
  ) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block });
      });
    });
  };

  // Abrir "Agendar nueva hora": el trigger vive debajo de "Próximas sesiones" y
  // en mobile (y en desktop chico) queda bajo el fold — sin scroll el formulario
  // se despliega fuera de vista y parece que el botón no hizo nada. El scroll se
  // dispara en onAnimationComplete del panel (no acá): antes de que el panel
  // termine de abrirse el documento es más corto que el viewport y el
  // scrollIntoView queda clampeado a mitad de camino. El cierre no scrollea.
  const openNewBookingPanel = () => {
    setShowNewBookingPanel(true);
  };

  const handleNewBookingPanelOpened = () => {
    if (showNewBookingPanel) scrollIntoViewSoon(newBookingSectionRef, 'start');
  };

  // ── Establecer terapeuta en contexto ──────────────────────────────────────

  useEffect(() => {
    if (state.selectedTherapist || terapeutasExtended.length === 0) return;
    const preferred = terapeutasExtended.find(t => t.id === patient?.therapistId);
    if (preferred) setTherapist(preferred);
  // terapeutasExtended.length y patient?.therapistId son primitivos estables
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terapeutasExtended.length, patient?.therapistId, !!state.selectedTherapist]);

  // ── Cargar consultas ──────────────────────────────────────────────────────

  const loadConsultations = useCallback(async () => {
    if (!patient?.id) return;
    setLoadingConsultations(true);
    try {
      const { supabase } = await import('../../lib/supabase/client');
      // FIX 4d PRODUCCIÓN: El SELECT directo a 'consultas' falla en producción para rol anon
      // porque RLS requiere auth.uid(). Usamos la RPC get_patient_consultations (SECURITY DEFINER)
      // que fue creada en sql/fix_fase2_2026_04.sql — DEBE aplicarse en Supabase SQL Editor.
      // SEC-057: la RPC exige p_patient_rut (gate de ownership server-side).
      const { data: allData, error } = await supabase
        .rpc('get_patient_consultations', { p_patient_id: patient.id, p_patient_rut: patient.rut });

      if (error) throw error;

      const now = new Date().toISOString();
      const all = (allData as Consultation[]) ?? [];

      setExistingConsultations(
        all.filter(c =>
          c.fecha_hora >= now &&
          ['programada', 'confirmada', 'agendada'].includes(c.estado)
        ).sort((a, b) => a.fecha_hora.localeCompare(b.fecha_hora))
      );
      setPastConsultations(
        all.filter(c =>
          c.fecha_hora < now &&
          ['completada', 'cancelada', 'no_asistio'].includes(c.estado)
        ).slice(0, 5)
      );
    } catch (error) {
      console.error('Error cargando consultas del paciente');
    } finally {
      setLoadingConsultations(false);
    }
  }, [patient?.id]);

  useEffect(() => {
    loadConsultations();
  }, [loadConsultations]);

  // ── Sincronizar servicio en contexto ─────────────────────────────────────

  // FUNC-105: la promo online depende de la fecha de la sesión elegida.
  const selectedSlotDate = state.selectedTimeSlot?.date ?? null;

  useEffect(() => {
    if (!state.selectedTherapist) return;
    const therapyObj = THERAPY_TYPES.find(t => t.id === selectedTherapyType);
    // Domicilio: precio fijo, ignora tarifa_diferenciada y la tabla normal — no
    // es un descuento sobre la sesión estándar, es un servicio distinto (recargo
    // por traslado del terapeuta). Lo resuelve getSessionPrice.
    const isDomicilio = selectedModality === 'domicilio';
    const effectiveDuration = isDomicilio ? 90 : selectedDuration;
    // FUNC-105/129: precio de lista según método + promo online; tarifa_diferenciada manda.
    const precioLista = getSessionPrice({
      tipo: selectedTherapyType, duracion: effectiveDuration, modalidad: selectedModality,
      metodo: state.metodoPagoDeclarado, fechaHora: selectedSlotDate,
    }) ?? 35000;

    setService({
      id: `${selectedTherapyType}-${effectiveDuration}-${selectedModality}`,
      therapyType: selectedTherapyType,
      name: therapyObj?.name ?? 'Terapia',
      duration: effectiveDuration,
      price: isDomicilio ? precioLista : (patient?.tarifa_diferenciada ?? precioLista),
      modality: selectedModality,
    });
  // state.selectedTherapist?.id (primitivo) evita re-fire por nueva referencia del objeto
  }, [selectedTherapyType, selectedDuration, selectedModality, state.selectedTherapist?.id, setService, patient?.tarifa_diferenciada, selectedSlotDate, state.metodoPagoDeclarado]);

  // Domicilio no tiene selector propio de duración — si queda seleccionada
  // cualquier otra cosa (ej. drift de estado), se vuelve a fijar en 90.
  useEffect(() => {
    if (selectedModality === 'domicilio' && selectedDuration !== 90) {
      setSelectedDuration(90);
    }
  }, [selectedModality, selectedDuration]);

  // Cada "vista" de este componente (calendario de nueva cita, calendario de
  // reagendamiento, síntesis) se monta cambiando estado local — NO el
  // `currentStep` del wizard — así que el scroll-to-top del BookingWizard no la
  // cubre. Sin esto la vista entra con la página scrolleada donde estaba el
  // botón que la abrió (fondo del formulario de "Agendar nueva hora"), y el
  // calendario aparece cortado por arriba: no se ve ni el mes ni los días de
  // la semana. Instantáneo, no smooth: es una pantalla nueva, no una
  // transición dentro de la misma.
  useEffect(() => {
    if (showCalendar || showRescheduleCalendar || showSynthesis) {
      window.scrollTo({ top: 0 });
    }
  }, [showCalendar, showRescheduleCalendar, showSynthesis]);

  // ── Toast helpers ─────────────────────────────────────────────────────────

  const showToast = (message: string, variant: Toast['variant'] = 'success') => {
    const id = ++toastCounter;
    setToasts(prev => [...prev, { id, message, variant }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  // ── Nudge nombre de la pareja (Feature 1 — §6.3) ──────────────────────────

  const handleDismissParejaNudge = () => {
    setNudgeDismissed(true);
  };

  const handleSaveNombrePareja = async () => {
    const trimmed = nudgeInput.trim();
    if (!trimmed || !patient) return;

    setSavingNudge(true);
    try {
      const result = await saveNombrePareja(patient.id, patient.rut, trimmed);
      if (result.ok) {
        setParejaNudge(false);
        try {
          window.localStorage.setItem(PAREJA_NUDGE_LOCALSTORAGE_KEY, 'done');
        } catch {
          /* incógnito / storage deshabilitado — es una conveniencia, se ignora en silencio */
        }
        showToast('Nombre de tu pareja guardado');
        setNudgeInput('');
      } else {
        showToast('No se pudo guardar. Intentá de nuevo.', 'error');
      }
    } catch {
      showToast('Error inesperado. Por favor contactá con soporte.', 'error');
    } finally {
      setSavingNudge(false);
    }
  };

  // ── Confirmar consulta ────────────────────────────────────────────────────

  const handleConfirmConsultation = async (consultation: Consultation) => {
    setConfirmModal(null);
    setActionInProgress(prev => ({ ...prev, [consultation.id]: true }));
    try {
      const { supabase } = await import('../../lib/supabase/client');
      const { error } = await supabase.rpc('confirm_patient_consultation', {
        p_consultation_id: consultation.id,
        p_patient_rut: patient?.rut ?? '',
      });

      if (error) {
        showToast('No se pudo confirmar la consulta. Intentá de nuevo.', 'error');
        return;
      }
      showToast('Consulta confirmada. Te enviaremos un recordatorio 24 horas antes.');
      await loadConsultations();
    } catch {
      showToast('Error inesperado. Por favor contactá con soporte.', 'error');
    } finally {
      setActionInProgress(prev => ({ ...prev, [consultation.id]: false }));
    }
  };

  // ── Cancelar consulta ─────────────────────────────────────────────────────

  const handleCancelConsultation = async (consultation: Consultation) => {
    setConfirmModal(null);
    const hoursUntil =
      (new Date(consultation.fecha_hora).getTime() - Date.now()) / (1000 * 60 * 60);

    if (hoursUntil < 24) {
      showToast('No es posible cancelar con menos de 24 horas de anticipación.', 'error');
      return;
    }

    setActionInProgress(prev => ({ ...prev, [consultation.id]: true }));
    try {
      const { supabase } = await import('../../lib/supabase/client');

      // Reembolsar anticipo si fue pagado con Transbank
      const { data: paymentRows } = await supabase
        .rpc('get_consulta_payment_info', { p_consulta_id: consultation.id });
      const consultaData = paymentRows?.[0];

      if (consultaData?.estado_pago_consulta === 'anticipo_pagado' && consultaData?.pago_anticipo_id) {
        try {
          // SEC-076: sin sesión en este flujo (paciente anónimo) — el gate de
          // ownership de la Edge Function se prueba con el RUT del paciente.
          await TransbankService.refund(consultaData.pago_anticipo_id, { patientRut: patient?.rut });
        } catch (refundErr) {
          console.error('[Cancel] Error en reembolso Transbank (no bloquea cancelación):', refundErr);
        }
      }

      const { error } = await supabase.rpc('cancel_patient_consultation', {
        p_consultation_id: consultation.id,
        p_patient_rut: patient?.rut ?? '',
      });

      if (error) {
        console.error('Error en cancel_patient_consultation:', error.message);
        // Substrings de los RAISE de cancel_patient_consultation (fuente de verdad en BD) — mantener en sync.
        const userMessage = error.message.includes('cobro registrado')
          ? 'Esta consulta tiene un cobro registrado. Para cancelarla, contactá a la clínica.'
          : error.message.includes('24 horas')
          ? 'No es posible cancelar con menos de 24 horas de anticipación.'
          : error.message.includes('estado actual')
          ? 'Esta consulta ya no se puede cancelar en su estado actual.'
          : 'No se pudo cancelar la consulta. Intentá de nuevo o contactá a la clínica.';
        showToast(userMessage, 'error');
        return;
      }

      showToast('Consulta cancelada. El horario quedó disponible para otros pacientes.');

      const dt = new Date(consultation.fecha_hora);
      EmailService.sendCancellationNotification({
        patientName:    patient ? `${patient.nombre} ${patient.apellido}` : '',
        ...patientEmailTarget(consultation.id),
        therapistName:  state.selectedTherapist?.name ?? '',
        therapistEmail: state.selectedTherapist?.email ?? 'angeldiazdiaz01@gmail.com',
        date:           dt.toLocaleDateString('en-CA', { timeZone: 'America/Santiago' }),
        time:           dt.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago', hour12: false }),
        modality:       consultation.modalidad,
        canceladoPor:   'paciente',
      }).catch(() => {});

      await loadConsultations();
    } catch {
      showToast('Error inesperado. Por favor contactá con soporte.', 'error');
    } finally {
      setActionInProgress(prev => ({ ...prev, [consultation.id]: false }));
    }
  };

  // ── Reagendamiento ────────────────────────────────────────────────────────

  const handleRescheduleStart = (consultation: Consultation) => {
    dispatch({ type: 'CLEAR_TIMESLOT' });
    // Preserve original consultation params so calendar loads correct duration/type
    const tipo: TherapyTypeId = consultation.tipo_consulta === 'pareja' ? 'pareja' : 'individual';
    const validDurations = DURATIONS_BY_TYPE[tipo] ?? DURATIONS_BY_TYPE.individual;
    // Si la duración original (ej. 50 min para una consulta individual creada antes de este fix)
    // ya no es una opción válida para el tipo, cae a la primera duración disponible.
    const matchedDuration = validDurations.find(d => d.minutes === consultation.duracion)?.minutes
      ?? (consultation.duracion === 90 ? 90 : validDurations[0].minutes);
    setSelectedDuration(matchedDuration);
    setSelectedTherapyType(tipo);
    // Preservar 'domicilio' al reagendar — antes colapsaba a 'presencial'.
    setSelectedModality(
      consultation.modalidad === 'domicilio' ? 'domicilio'
        : consultation.modalidad === 'online' ? 'online'
        : 'presencial'
    );
    setConsultationToReschedule(consultation);
    setShowRescheduleCalendar(true);
    setIsRescheduling(true);
  };

  const handleRescheduleCancel = () => {
    dispatch({ type: 'CLEAR_TIMESLOT' });
    setConsultationToReschedule(null);
    setShowRescheduleCalendar(false);
    setIsRescheduling(false);
  };

  const handleRescheduleConfirm = async () => {
    if (!consultationToReschedule || !state.selectedTimeSlot) {
      showToast('Por favor seleccioná una nueva fecha y hora.', 'error');
      return;
    }
    setIsBooking(true);
    try {
      const result = await RescheduleService.rescheduleConsultation({
        consultationId: consultationToReschedule.id,
        newDate:        state.selectedTimeSlot.date,
        newTime:        state.selectedTimeSlot.time,
        patientRut:     patient?.rut ?? '',
        therapistId:    consultationToReschedule.terapeuta_id ?? '',
        patientName:    patient ? `${patient.nombre} ${patient.apellido}` : undefined,
        therapistName:  state.selectedTherapist?.name,
        therapistEmail: state.selectedTherapist?.email,
        oldDateTime:    consultationToReschedule.fecha_hora,
      });

      if (result.success) {
        showToast('Cita reagendada exitosamente. Recibirás una confirmación por email.');
        await loadConsultations();
        handleRescheduleCancel();
      } else {
        showToast(result.error ?? 'No se pudo reagendar la cita. Intentá con otra fecha.', 'error');
      }
    } catch {
      showToast('Error inesperado al reagendar. Por favor contactá con soporte.', 'error');
    } finally {
      setIsBooking(false);
    }
  };

  // ── Nueva cita ────────────────────────────────────────────────────────────

  const handleCompleteBooking = async () => {
    if (!patient) return;
    if (!state.selectedTimeSlot) {
      showToast('Seleccioná una fecha y hora antes de confirmar.', 'error');
      return;
    }
    const therapistToUse = state.selectedTherapist;
    if (!therapistToUse) {
      showToast('No se encontró el terapeuta asignado. Por favor recargá la página.', 'error');
      return;
    }
    setIsBooking(true);
    try {
      const therapyObj = THERAPY_TYPES.find(t => t.id === selectedTherapyType);
      const isDomicilio = selectedModality === 'domicilio';
      const effectiveDuration = isDomicilio ? 90 : selectedDuration;
      const precioLista = getSessionPrice({
        tipo: selectedTherapyType, duracion: effectiveDuration, modalidad: selectedModality,
        metodo: state.metodoPagoDeclarado, fechaHora: state.selectedTimeSlot.date,
      }) ?? 35000;
      const effectivePrice = isDomicilio ? precioLista : (patient?.tarifa_diferenciada ?? precioLista);

      const bookingData = {
        therapist: therapistToUse,
        service: {
          id: `${selectedTherapyType}-${effectiveDuration}-${selectedModality}`,
          therapyType: selectedTherapyType,
          name: therapyObj?.name ?? 'Terapia',
          duration: effectiveDuration,
          price: effectivePrice,
          modality: selectedModality,
        },
        timeSlot: {
          id: `${state.selectedTimeSlot.date}-${state.selectedTimeSlot.time}`,
          date: state.selectedTimeSlot.date,
          time: state.selectedTimeSlot.time,
        },
        patientData: {
          nombre: patient.nombre,
          apellido: patient.apellido,
          email: '',
          telefono: '',
          rut: patient.rut,
          fechaNacimiento: '1990-01-01',
          genero: 'prefiero-no-decir' as const,
          antecedentesMedicos: '',
          medicamentos: '',
          emergenciaContacto: patient.apellido ?? 'Contacto',
          emergenciaTelefono: '',
          preferenciaComunicacion: 'email' as const,
          motivoConsulta: 'Consulta de seguimiento — Paciente existente',
          // Paciente existente: el consentimiento de datos clínicos ya quedó
          // registrado cuando se creó (FUNC-114). No se asume opt-in de
          // marketing acá — nunca hubo checkbox para eso en este flujo.
          consentimientoDatosClinicos: true,
          consentimientoContactoMarketing: false,
          terminosCondiciones: true,
        },
        metodoPagoDeclarado: state.metodoPagoDeclarado,
        // FUNC-126: el paciente no tipea contacto → no se llama
        // create_patient_simple (exige email); se usa el id ya resuelto por
        // search_patient_by_rut (mismo id que esa RPC devolvería por RUT).
        existingPatientId: patient.id,
      };

      const result = await BookingServiceFixed.processExistingPatientBooking(bookingData as any);

      if (result?.success) {
        const therapyObj  = THERAPY_TYPES.find(t => t.id === selectedTherapyType);

        await EmailService.sendBookingConfirmation({
          bookingId:        result.bookingId,
          patientName:      `${patient.nombre} ${patient.apellido}`,
          ...patientEmailTarget(result.consultaId ?? ''),
          therapistName:    therapistToUse.name,
          therapistEmail:   therapistToUse.email ?? 'angeldiazdiaz01@gmail.com',
          therapistRegistroSuperintendencia: therapistToUse.professionalInfo?.registroSuperintendencia,
          therapistNumeroLicencia: therapistToUse.professionalInfo?.licenseNumber,
          serviceName:      therapyObj?.name ?? 'Terapia',
          therapyType:      selectedTherapyType,
          date:             state.selectedTimeSlot.date,
          time:             state.selectedTimeSlot.time,
          duration:         effectiveDuration,
          price:            effectivePrice,
          modality:         selectedModality,
          meetLink:         result.meetLink,
          metodoPagoDeclarado: state.metodoPagoDeclarado,
        });

        onComplete(result.bookingId, result.meetLink);
      } else {
        showToast(result?.error ?? 'No se pudo crear la cita. Intentá nuevamente.', 'error');
      }
    } catch {
      showToast('Error inesperado al crear la cita. Por favor contactá con soporte.', 'error');
    } finally {
      setIsBooking(false);
    }
  };

  // ── Precio actual ─────────────────────────────────────────────────────────

  const precioListaActual = getSessionPrice({
    tipo: selectedTherapyType, duracion: selectedDuration, modalidad: selectedModality,
    metodo: state.metodoPagoDeclarado, fechaHora: selectedSlotDate,
  }) ?? 35000;
  const currentPrice = selectedModality === 'domicilio'
    ? precioListaActual
    : patient?.tarifa_diferenciada ?? precioListaActual;

  // ─── Early returns ─────────────────────────────────────────────────────────

  if (!patient || terapeutasLoading) {
    return (
      <div className="epb-loading" aria-live="polite">
        <div className="epb-spinner" aria-hidden="true" />
        <p>Cargando tu información...</p>
      </div>
    );
  }

  // Vista de reagendamiento — pantalla completa del calendario
  if (showRescheduleCalendar && consultationToReschedule) {
    return (
      <div className="epb-calendar-view">
        <div className="epb-calendar-topbar">
          <button className="epb-back-btn" onClick={handleRescheduleCancel}>
            <i className="fas fa-arrow-left" aria-hidden="true" />
            Cancelar reagendamiento
          </button>
          <div className="epb-reschedule-info">
            <p className="epb-reschedule-label">Reagendando cita del</p>
            <strong>
              {formatDate(consultationToReschedule.fecha_hora)} a las{' '}
              {formatTime(consultationToReschedule.fecha_hora)}
            </strong>
          </div>
        </div>

        <CalendarSelectorImproved
          onNext={() => {/* el botón de confirmación aparece abajo */}}
          onPrevious={handleRescheduleCancel}
          hideNavigation={true}
        />

        {state.selectedTimeSlot && (
          <div className="epb-reschedule-confirm">
            <div className="epb-change-summary">
              <div className="epb-change-item epb-change-item--old">
                <span className="epb-change-label">Cita actual</span>
                <span className="epb-change-value">
                  {formatDate(consultationToReschedule.fecha_hora)}{' '}
                  {formatTime(consultationToReschedule.fecha_hora)}
                </span>
              </div>
              <i className="fas fa-arrow-right epb-change-arrow" aria-hidden="true" />
              <div className="epb-change-item epb-change-item--new">
                <span className="epb-change-label">Nueva cita</span>
                <span className="epb-change-value">
                  {formatDate(state.selectedTimeSlot.date)} {state.selectedTimeSlot.time}
                </span>
              </div>
            </div>
            <div className="epb-reschedule-actions">
              <button
                className="epb-btn epb-btn--ghost"
                onClick={handleRescheduleCancel}
                disabled={isBooking}
              >
                Cancelar
              </button>
              <button
                className="epb-btn epb-btn--primary"
                onClick={handleRescheduleConfirm}
                disabled={isBooking}
              >
                {isBooking ? (
                  <>
                    <span className="epb-spinner epb-spinner--sm" aria-hidden="true" />
                    Reagendando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check" aria-hidden="true" />
                    Confirmar reagendamiento
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vista del calendario para nueva cita
  if (showCalendar) {
    return (
      <div className="epb-calendar-view">
        <div className="epb-calendar-topbar">
          <button className="epb-back-btn" onClick={() => setShowCalendar(false)}>
            <i className="fas fa-arrow-left" aria-hidden="true" />
            Volver
          </button>
          <h3 className="epb-calendar-title">Seleccioná fecha y hora</h3>
        </div>

        <CalendarSelectorImproved
          onNext={() => {
            setShowCalendar(false);
            setShowSynthesis(true);
          }}
          onPrevious={() => setShowCalendar(false)}
          hideNavigation={true}
        />
      </div>
    );
  }

  // ─── Vista de síntesis — revisión antes de confirmar ──────────────────────

  if (showSynthesis && state.selectedTimeSlot) {
    return (
      <motion.div
        className="epb-synthesis-view"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="epb-synthesis-header">
          <button className="epb-back-btn" onClick={() => setShowSynthesis(false)}>
            <i className="fas fa-arrow-left" aria-hidden="true" />
            Volver
          </button>
          <h2 className="epb-synthesis-title">Revisá tu cita</h2>
          <p className="epb-synthesis-subtitle">Casi terminamos, confirmá los detalles</p>
        </div>

        <div className="epb-synthesis-card">
          {/* Terapeuta */}
          <div className="epb-synthesis-row">
            <i className="fas fa-user-md" aria-hidden="true" />
            <div>
              <span className="epb-synthesis-label">Terapeuta</span>
              <span className="epb-synthesis-value">{state.selectedTherapist?.name}</span>
            </div>
          </div>
          {/* Tipo y duración */}
          <div className="epb-synthesis-row">
            <i className="fas fa-stethoscope" aria-hidden="true" />
            <div>
              <span className="epb-synthesis-label">Consulta</span>
              <span className="epb-synthesis-value">
                {THERAPY_TYPES.find(t => t.id === selectedTherapyType)?.name} · {selectedDuration} min
              </span>
            </div>
          </div>
          {/* Modalidad */}
          <div className="epb-synthesis-row">
            <i className={`fas ${modalityIcon(selectedModality)}`} aria-hidden="true" />
            <div>
              <span className="epb-synthesis-label">Modalidad</span>
              <span className="epb-synthesis-value">{modalityLabel(selectedModality)}</span>
            </div>
          </div>
          {/* Fecha y hora */}
          <div className="epb-synthesis-row">
            <i className="fas fa-calendar-alt" aria-hidden="true" />
            <div>
              <span className="epb-synthesis-label">Fecha y hora</span>
              <span className="epb-synthesis-value">
                {formatDate(state.selectedTimeSlot.date)} a las {state.selectedTimeSlot.time}
              </span>
              <button
                className="epb-change-slot"
                onClick={() => { setShowSynthesis(false); setShowCalendar(true); }}
              >
                Cambiar
              </button>
            </div>
          </div>
          {/* Precio */}
          <div className="epb-synthesis-row epb-synthesis-row--price">
            <i className="fas fa-tag" aria-hidden="true" />
            <div>
              <span className="epb-synthesis-label">Total</span>
              <span className="epb-synthesis-value">${currentPrice.toLocaleString()}</span>
            </div>
          </div>

          {/* Método de pago */}
          <div className="epb-synthesis-row">
            <i className="fas fa-wallet" aria-hidden="true" />
            <div>
              <span className="epb-synthesis-label">Método de pago</span>
              <PaymentMethodSelector
                metodo={state.metodoPagoDeclarado}
                onChange={setMetodoPagoDeclarado}
                tipo={selectedTherapyType}
                duracion={selectedModality === 'domicilio' ? 90 : selectedDuration}
                modalidad={selectedModality}
                fechaHora={selectedSlotDate}
                tarifaFija={selectedModality !== 'domicilio' && !!patient?.tarifa_diferenciada}
              />
            </div>
          </div>
        </div>

        <div className="epb-synthesis-actions">
          <button
            className="epb-btn epb-btn--primary epb-btn--full"
            onClick={handleCompleteBooking}
            disabled={isBooking}
          >
            {isBooking ? (
              <>
                <span className="epb-spinner epb-spinner--sm" aria-hidden="true" />
                Confirmando...
              </>
            ) : (
              <>
                <i className="fas fa-check" aria-hidden="true" />
                Confirmar cita
              </>
            )}
          </button>
        </div>
      </motion.div>
    );
  }

  // ─── Vista principal — dashboard ───────────────────────────────────────────

  return (
    <div className="epb-root">

      {/* ── Sección 1: Bienvenida personalizada ── */}
      <motion.section
        className="epb-welcome"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* BUG 4a FIX: clearBooking antes de onBack para que PatientVerification
            arranque limpio y no redirija automáticamente al flujo de paciente existente */}
        <button className="epb-back-btn epb-back-btn--inline" onClick={() => { clearBooking(); onBack(); }}>
          <i className="fas fa-arrow-left" aria-hidden="true" />
          Volver
        </button>

        <div className="epb-welcome-body">
          <div className="epb-avatar" aria-hidden="true">
            {patient.nombre.charAt(0).toUpperCase()}
          </div>
          <div className="epb-welcome-text">
            <h1 className="epb-welcome-title">
              Hola de nuevo, <span>{patient.nombre}</span>
            </h1>
            <p className="epb-welcome-rut">RUT {patient.rut}</p>
            {preferredTherapist && (
              <p className="epb-welcome-therapist">
                <i className="fas fa-user-md" aria-hidden="true" />
                Tu terapeuta: <strong>{preferredTherapist.name}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Nudge nombre de la pareja — Feature 1 (§6.3), Fase B de Konex */}
        <AnimatePresence>
          {showParejaNudgeCard && (
            <motion.div
              className="epb-pareja-nudge"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              role="region"
              aria-label="Nombre de tu pareja"
            >
              <div className="epb-pareja-nudge__inner">
                <i className="fas fa-heart epb-pareja-nudge__icon" aria-hidden="true" />
                <div className="epb-pareja-nudge__body">
                  <p className="epb-pareja-nudge__text">
                    Nos falta el nombre de tu pareja. Si hiciste o vas a hacer terapia de
                    parejas, agregalo acá.
                  </p>
                  <div className="epb-pareja-nudge__form">
                    <input
                      type="text"
                      className="epb-pareja-nudge__input"
                      placeholder="Nombre de tu pareja"
                      value={nudgeInput}
                      onChange={e => setNudgeInput(e.target.value)}
                      maxLength={120}
                      disabled={savingNudge}
                      aria-label="Nombre de tu pareja"
                    />
                    <div className="epb-pareja-nudge__actions">
                      <button
                        type="button"
                        className="epb-btn epb-btn--ghost"
                        onClick={handleDismissParejaNudge}
                        disabled={savingNudge}
                      >
                        Ahora no
                      </button>
                      <button
                        type="button"
                        className="epb-btn epb-btn--secondary"
                        onClick={handleSaveNombrePareja}
                        disabled={savingNudge || !nudgeInput.trim()}
                      >
                        {savingNudge ? (
                          <span className="epb-spinner epb-spinner--sm" aria-hidden="true" />
                        ) : (
                          'Guardar'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>

      {/* ── Sección 2: Próximas sesiones ── */}
      <motion.section
        className="epb-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.08 }}
      >
        <div className="epb-section-header">
          <h2 className="epb-section-title">
            <i className="fas fa-calendar-check" aria-hidden="true" />
            Próximas sesiones
          </h2>
          {existingConsultations.length > 0 && (
            <span className="epb-badge">
              {existingConsultations.length} programada{existingConsultations.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {loadingConsultations ? (
          <div className="epb-loading-inline" aria-live="polite">
            <span className="epb-spinner epb-spinner--sm" aria-hidden="true" />
            Consultando tus citas...
          </div>
        ) : existingConsultations.length === 0 ? (
          <div className="epb-empty-state">
            <i className="fas fa-calendar-plus epb-empty-icon" aria-hidden="true" />
            <p>No tenés sesiones programadas.</p>
            <button
              className="epb-btn epb-btn--secondary"
              onClick={openNewBookingPanel}
            >
              Agendar ahora
            </button>
          </div>
        ) : (
          <div className="epb-cards-scroll">
            {existingConsultations.map((c, index) => {
              const isToday = new Date(c.fecha_hora).toDateString() === new Date().toDateString();
              const isTomorrow =
                new Date(new Date(c.fecha_hora).getTime() - 86400000).toDateString() ===
                new Date().toDateString();
              const tColor = therapyColor(c.tipo_consulta);

              return (
                <motion.div
                  key={c.id}
                  className={`epb-appointment-card${isToday ? ' epb-appointment-card--today' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.06 }}
                >
                  {(isToday || isTomorrow) && (
                    <div className={`epb-soon-badge${isToday ? ' epb-soon-badge--today' : ''}`}>
                      {isToday ? 'Hoy' : 'Mañana'}
                    </div>
                  )}

                  <div className="epb-card-main">
                    <div className="epb-card-date">
                      <span className="epb-card-day">
                        {new Date(c.fecha_hora).toLocaleDateString('es-CL', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                      <span className="epb-card-weekday">
                        {new Date(c.fecha_hora).toLocaleDateString('es-CL', { weekday: 'long' })}
                      </span>
                    </div>

                    <div className="epb-card-time">
                      <span className="epb-card-hour">{formatTime(c.fecha_hora)}</span>
                      <span className="epb-card-duration">{c.duracion} min</span>
                    </div>
                  </div>

                  <div className="epb-card-tags">
                    <span
                      className="epb-therapy-badge"
                      style={{ '--therapy-color': tColor } as React.CSSProperties}
                    >
                      {c.tipo_consulta}
                    </span>
                    <span className="epb-modality-badge">
                      <i
                        className={`fas ${modalityIcon(c.modalidad)}`}
                        aria-hidden="true"
                      />
                      {modalityLabel(c.modalidad)}
                    </span>
                    <span className={`epb-status-badge epb-status-badge--${c.estado}`}>
                      {c.estado === 'programada' ? 'Programada' : c.estado.charAt(0).toUpperCase() + c.estado.slice(1)}
                    </span>
                  </div>

                  <div className="epb-card-actions">
                    {(c.estado === 'programada') && (
                      <button
                        className="epb-action-btn epb-action-btn--confirm"
                        onClick={() => setConfirmModal({ type: 'confirm', consultation: c })}
                        disabled={actionInProgress[c.id] || isRescheduling}
                        aria-label="Confirmar consulta"
                      >
                        <i className="fas fa-check" aria-hidden="true" />
                        Confirmar
                      </button>
                    )}

                    {(['programada', 'confirmada', 'agendada'].includes(c.estado)) && (
                      <>
                        <button
                          className="epb-action-btn epb-action-btn--reschedule"
                          onClick={() => handleRescheduleStart(c)}
                          disabled={actionInProgress[c.id] || isRescheduling}
                          aria-label="Reagendar consulta"
                        >
                          <i className="fas fa-calendar-alt" aria-hidden="true" />
                          Reagendar
                        </button>
                        <button
                          className="epb-action-btn epb-action-btn--cancel"
                          onClick={() => setConfirmModal({ type: 'cancel', consultation: c })}
                          disabled={actionInProgress[c.id] || isRescheduling}
                          aria-label="Cancelar consulta"
                        >
                          {actionInProgress[c.id] ? (
                            <span className="epb-spinner epb-spinner--sm" aria-hidden="true" />
                          ) : (
                            <i className="fas fa-times" aria-hidden="true" />
                          )}
                          Cancelar
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.section>

      {/* ── Sección 3: Agendar nueva hora ── */}
      <motion.section
        ref={newBookingSectionRef}
        className="epb-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.16 }}
      >
        <button
          className="epb-new-booking-trigger"
          onClick={() => (showNewBookingPanel ? setShowNewBookingPanel(false) : openNewBookingPanel())}
          aria-expanded={showNewBookingPanel}
        >
          <span className="epb-new-booking-label">
            <i className="fas fa-plus-circle" aria-hidden="true" />
            Agendar nueva hora
          </span>
          <i
            className={`fas fa-chevron-${showNewBookingPanel ? 'up' : 'down'} epb-chevron`}
            aria-hidden="true"
          />
        </button>

        <AnimatePresence>
          {showNewBookingPanel && (
            <motion.div
              className="epb-new-booking-panel"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              onAnimationComplete={handleNewBookingPanelOpened}
            >
              {/* Tipo de terapia */}
              <div className="epb-form-group">
                <label className="epb-form-label">Tipo de terapia</label>
                <div className="epb-therapy-grid">
                  {THERAPY_TYPES.map(t => (
                    <button
                      key={t.id}
                      className={`epb-option-card${selectedTherapyType === t.id ? ' epb-option-card--selected' : ''}`}
                      onClick={() => {
                        setSelectedTherapyType(t.id);
                        // Cada tipo tiene su propia tabla de duraciones (individual usa 45/90,
                        // pareja/familia usan 50/90) — si la duración actual no es válida para
                        // el nuevo tipo, ningún botón de duración quedaría seleccionado.
                        if (!DURATIONS_BY_TYPE[t.id].some(d => d.minutes === selectedDuration)) {
                          setSelectedDuration(DURATIONS_BY_TYPE[t.id][0].minutes);
                        }
                        // Domicilio solo existe para pareja.
                        if (t.id !== 'pareja' && selectedModality === 'domicilio') {
                          setSelectedModality('presencial');
                        }
                      }}
                      style={
                        selectedTherapyType === t.id
                          ? ({ '--option-color': t.color } as React.CSSProperties)
                          : undefined
                      }
                    >
                      <i className={t.icon} aria-hidden="true" style={{ color: t.color }} />
                      <span>{t.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Modalidad */}
              <div className="epb-form-group">
                <label className="epb-form-label">Modalidad</label>
                <div className={`epb-modality-grid${selectedTherapyType === 'pareja' ? ' epb-modality-grid--three' : ''}`}>
                  {MODALITY_OPTIONS.filter(m => m.id !== 'domicilio' || selectedTherapyType === 'pareja').map(m => (
                    <button
                      key={m.id}
                      className={`epb-option-card${selectedModality === m.id ? ' epb-option-card--selected' : ''}`}
                      onClick={() => setSelectedModality(m.id)}
                    >
                      <i className={m.icon} aria-hidden="true" />
                      <span className="epb-option-name">{m.name}</span>
                      <span className="epb-option-desc">{m.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Ubicación (presencial) */}
              {selectedModality === 'presencial' && (
                <div className="epb-form-group">
                  <label className="epb-form-label" htmlFor="epb-location">
                    <i className="fas fa-map-marker-alt" aria-hidden="true" />
                    Ubicación
                  </label>
                  <select
                    id="epb-location"
                    className="epb-select"
                    value={selectedLocation}
                    onChange={e => setSelectedLocation(e.target.value)}
                  >
                    {LOCATIONS.map(l => (
                      <option key={l.id} value={l.id}>
                        {l.name} — {l.address}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Duración — domicilio la fija en 90min, sin selector */}
              <div className="epb-form-group">
                <label className="epb-form-label">Duración</label>
                <div className="epb-duration-grid">
                  {selectedModality === 'domicilio' ? (
                    <div className="epb-option-card epb-option-card--duration epb-option-card--selected">
                      <span className="epb-option-name">90 minutos</span>
                      <span className="epb-option-price">${(getSessionPrice({ tipo: selectedTherapyType, duracion: 90, modalidad: 'domicilio', metodo: state.metodoPagoDeclarado }) ?? 0).toLocaleString('es-CL')}</span>
                    </div>
                  ) : (
                    DURATIONS_BY_TYPE[selectedTherapyType].map(d => (
                      <button
                        key={d.minutes}
                        className={`epb-option-card epb-option-card--duration${selectedDuration === d.minutes ? ' epb-option-card--selected' : ''}`}
                        onClick={() => setSelectedDuration(d.minutes)}
                      >
                        <span className="epb-option-name">{d.name}</span>
                        <span className="epb-option-price">${(getSessionPrice({ tipo: selectedTherapyType, duracion: d.minutes, modalidad: selectedModality, metodo: state.metodoPagoDeclarado, fechaHora: selectedSlotDate }) ?? 0).toLocaleString('es-CL')}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Resumen y acciones */}
              <div className="epb-summary-bar">
                <div className="epb-summary-meta">
                  <span>{THERAPY_TYPES.find(t => t.id === selectedTherapyType)?.name}</span>
                  <span>·</span>
                  <span>{selectedDuration} min</span>
                  <span>·</span>
                  <span>{modalityLabel(selectedModality)}</span>
                </div>
                <div className="epb-summary-price">
                  <span className="epb-price-label">Total</span>
                  <span className="epb-price-value">${currentPrice.toLocaleString()}</span>
                </div>
              </div>

              {!state.selectedTimeSlot ? (
                <button
                  className="epb-btn epb-btn--secondary epb-btn--full"
                  onClick={() => setShowCalendar(true)}
                >
                  <i className="fas fa-calendar" aria-hidden="true" />
                  Seleccionar fecha y hora
                </button>
              ) : (
                <div className="epb-timeslot-confirm">
                  <div className="epb-timeslot-info">
                    <i className="fas fa-calendar-check" aria-hidden="true" />
                    <span>
                      {formatDate(state.selectedTimeSlot.date)} a las{' '}
                      {state.selectedTimeSlot.time}
                    </span>
                    <button
                      className="epb-change-slot"
                      onClick={() => setShowCalendar(true)}
                    >
                      Cambiar
                    </button>
                  </div>
                  <button
                    className="epb-btn epb-btn--primary epb-btn--full"
                    onClick={() => setShowSynthesis(true)}
                    disabled={isBooking}
                  >
                    <i className="fas fa-check" aria-hidden="true" />
                    Confirmar cita
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>

      {/* ── Sección 4: Historial ── */}
      {!loadingConsultations && (
        <motion.section
          className="epb-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.24 }}
        >
          <button
            className="epb-historial-toggle"
            onClick={() => setHistorialExpanded(prev => !prev)}
            aria-expanded={historialExpanded}
          >
            <span>
              <i className="fas fa-history" aria-hidden="true" />
              Historial de sesiones
              <span className="epb-historial-count">({pastConsultations.length})</span>
            </span>
            <i
              className={`fas fa-chevron-${historialExpanded ? 'up' : 'down'}`}
              aria-hidden="true"
            />
          </button>

          <AnimatePresence>
            {historialExpanded && pastConsultations.length === 0 && (
              <motion.div
                className="epb-historial-empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <p>No hay sesiones anteriores registradas.</p>
              </motion.div>
            )}
            {historialExpanded && pastConsultations.length > 0 && (
              <motion.ul
                className="epb-historial-list"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
              >
                {pastConsultations.map(c => (
                  <li key={c.id} className="epb-historial-item">
                    <div className="epb-historial-date">
                      <span>{formatDate(c.fecha_hora)}</span>
                      <span className="epb-historial-time">{formatTime(c.fecha_hora)}</span>
                    </div>
                    <div className="epb-historial-detail">
                      <span>{c.tipo_consulta}</span>
                      <span className="epb-historial-modality">
                        {modalityLabel(c.modalidad)}
                      </span>
                    </div>
                    <span className={`epb-status-badge epb-status-badge--${c.estado}`}>
                      <i
                        className={`fas ${
                          c.estado === 'completada'
                            ? 'fa-check-circle'
                            : c.estado === 'cancelada'
                            ? 'fa-times-circle'
                            : 'fa-minus-circle'
                        }`}
                        aria-hidden="true"
                      />
                      {c.estado === 'completada'
                        ? 'Completada'
                        : c.estado === 'cancelada'
                        ? 'Cancelada'
                        : 'No asistió'}
                    </span>
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </motion.section>
      )}

      {/* ── Modal de confirmación inline ── */}
      <AnimatePresence>
        {confirmModal && (
          <motion.div
            className="epb-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirmModal(null)}
            role="dialog"
            aria-modal="true"
            aria-label={
              confirmModal.type === 'confirm' ? 'Confirmar consulta' : 'Cancelar consulta'
            }
          >
            <motion.div
              className="epb-modal-card"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
            >
              <div className={`epb-modal-icon epb-modal-icon--${confirmModal.type}`}>
                <i
                  className={`fas ${
                    confirmModal.type === 'confirm' ? 'fa-check-circle' : 'fa-exclamation-triangle'
                  }`}
                  aria-hidden="true"
                />
              </div>

              <h3 className="epb-modal-title">
                {confirmModal.type === 'confirm' ? '¿Confirmás tu consulta?' : '¿Cancelás tu consulta?'}
              </h3>

              <p className="epb-modal-body">
                {confirmModal.type === 'confirm' ? (
                  <>
                    Confirmarás tu sesión del{' '}
                    <strong>
                      {formatDate(confirmModal.consultation.fecha_hora)} a las{' '}
                      {formatTime(confirmModal.consultation.fecha_hora)}
                    </strong>
                    . Te enviaremos un recordatorio 24 horas antes.
                  </>
                ) : (
                  <>
                    Estás por cancelar la sesión del{' '}
                    <strong>
                      {formatDate(confirmModal.consultation.fecha_hora)} a las{' '}
                      {formatTime(confirmModal.consultation.fecha_hora)}
                    </strong>
                    . Esta acción liberará el horario para otros pacientes.
                  </>
                )}
              </p>

              <div className="epb-modal-actions">
                <button
                  className="epb-btn epb-btn--ghost"
                  onClick={() => setConfirmModal(null)}
                >
                  Volver
                </button>
                <button
                  className={`epb-btn ${
                    confirmModal.type === 'confirm' ? 'epb-btn--primary' : 'epb-btn--danger'
                  }`}
                  onClick={() =>
                    confirmModal.type === 'confirm'
                      ? handleConfirmConsultation(confirmModal.consultation)
                      : handleCancelConsultation(confirmModal.consultation)
                  }
                >
                  {confirmModal.type === 'confirm' ? 'Confirmar' : 'Cancelar consulta'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Toast stack ── */}
      <div className="epb-toast-stack" aria-live="polite" aria-atomic="false">
        <AnimatePresence>
          {toasts.map(toast => (
            <motion.div
              key={toast.id}
              className={`epb-toast epb-toast--${toast.variant}`}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <i
                className={`fas ${toast.variant === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}
                aria-hidden="true"
              />
              {toast.message}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ExistingPatientBooking;
